import React, { useState, useEffect } from 'react';
import './App.css';
import ReactMarkdown from 'react-markdown';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { matchSorter } from 'match-sorter';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import SimilarCandidatesPage from './SimilarCandidatesPage';
import SimilarMandatesPage from './SimilarMandatesPage';
import Header from './components/Header';
import SearchForm from './components/SearchForm';
import NaturalLanguageSearch from './components/NaturalLanguageSearch';
import TabSystem from './components/TabSystem';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import apiService from './services/apiService';
import config from './config';

function AppContent() {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('mandate');
  const [mandateId, setMandateId] = useState('');
  const [results, setResults] = useState(null); // stores the full API response
  const [allCandidates, setAllCandidates] = useState([]); // all candidates fetched
  const [displayedCandidates, setDisplayedCandidates] = useState([]); // candidates currently shown
  const [displayCount, setDisplayCount] = useState(20); // how many to show
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [recentMandates, setRecentMandates] = useState([]);
  const [allMandates, setAllMandates] = useState([]);

  const [linkedCandidates, setLinkedCandidates] = useState([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [linkedError, setLinkedError] = useState('');
  const [linkedMandates, setLinkedMandates] = useState([]);
  const [loadingLinkedMandates, setLoadingLinkedMandates] = useState(false);
  const [linkedMandatesError, setLinkedMandatesError] = useState('');
  const [mandateLinkedCandidates, setMandateLinkedCandidates] = useState([]);
  const [loadingMandateLinked, setLoadingMandateLinked] = useState(false);
  const [mandateLinkedError, setMandateLinkedError] = useState('');
  const [linkedCandidatesScores, setLinkedCandidatesScores] = useState({});
  const [showLinkedCandidates, setShowLinkedCandidates] = useState(false);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Reset results when switching tabs
  useEffect(() => {
    setResults(null);
    setAllCandidates([]);
    setDisplayedCandidates([]);
    setDisplayCount(20);
    setError('');
    setShowLinkedCandidates(false);
    setMandateId(''); // Reset mandate ID when switching to NLP
  }, [activeTab]);

  // Fetch recent mandates on mount - only when user is authenticated
  useEffect(() => {
    if (authLoading || !user) return; // Wait for auth to complete and user to be available
    
    apiService.getRecentMandates()
      .then(data => {
        console.log("recent_mandates response:", data);
        if (Array.isArray(data)) {
          setRecentMandates(data);
        } else {
          setRecentMandates([]); // fallback to empty array
          // Optionally, handle error here
        }
      })
      .catch(err => {
        console.error('Failed to fetch recent mandates:', err);
        setRecentMandates([]); // fallback to empty array on error
      });
  }, [user, authLoading]);

  useEffect(() => {
    if (authLoading || !user) return; // Wait for auth to complete and user to be available
    
    apiService.getAllMandates()
      .then(data => {
        if (Array.isArray(data)) setAllMandates(data);
        else setAllMandates([]);
      })
      .catch(() => setAllMandates([]));
  }, [user, authLoading]);

  // Handle automatic search from URL parameters
  useEffect(() => {
    if (authLoading || !user) return; // Wait for auth to complete and user to be available
    
    const searchMandateId = searchParams.get('search');
    if (searchMandateId) {
      setMandateId(searchMandateId);
      // Clear the search parameter from URL
      setSearchParams({});
      
      // Perform search directly
      const performAutoSearch = async () => {
        setLoading(true);
        setError('');
        setResults(null);
        setAllCandidates([]);
        setDisplayedCandidates([]);
        setDisplayCount(20);
        setShowLinkedCandidates(false);
        
        try {
          const data = await apiService.searchByMandate(parseInt(searchMandateId, 10), 200);
          setResults(data);
          setAllCandidates(data.results);
          setDisplayedCandidates(data.results.slice(0, 20));
          
          // Automatically fetch linked candidates after successful search
          if (data && data.mandate && data.mandate.id) {
            try {
              const linkedData = await apiService.getMandateLinkedCandidates(data.mandate.id);
              setMandateLinkedCandidates(linkedData.candidates || []);
            } catch (linkedErr) {
              console.error('Failed to fetch linked candidates:', linkedErr);
              setMandateLinkedCandidates([]);
            }
          }
        } catch (err) {
          setError(`Search failed: ${err.message}`);
        } finally {
          setLoading(false);
        }
      };
      
      performAutoSearch();
    }
  }, [searchParams, setSearchParams, user, authLoading]);

  const handleInputChange = (e) => {
    setMandateId(e.target.value);
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!mandateId.trim()) {
      setError('Please enter a mandate ID');
      return;
    }
    setLoading(true);
    setError('');
    setResults(null);
    setAllCandidates([]);
    setDisplayedCandidates([]);
    setDisplayCount(20);
    setShowLinkedCandidates(false); // Hide linked candidates modal on new search
    try {
      const data = await apiService.searchByMandate(parseInt(mandateId, 10), 200);
      setResults(data);
      setAllCandidates(data.results);
      setDisplayedCandidates(data.results.slice(0, 20));
      
      // Automatically fetch linked candidates after successful search
      if (data && data.mandate && data.mandate.id) {
        try {
          const linkedData = await apiService.getMandateLinkedCandidates(data.mandate.id);
          setMandateLinkedCandidates(linkedData.candidates || []);
        } catch (linkedErr) {
          console.error('Failed to fetch linked candidates:', linkedErr);
          setMandateLinkedCandidates([]);
        }
      }
    } catch (err) {
      setError(`Search failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    const newDisplayCount = displayCount + 20;
    setDisplayedCandidates(allCandidates.slice(0, newDisplayCount));
    setDisplayCount(newDisplayCount);
  };

  const resetForm = () => {
    setMandateId('');
    setResults(null);
    setAllCandidates([]);
    setDisplayedCandidates([]);
    setDisplayCount(20);
    setError('');
    setShowLinkedCandidates(false); // Hide linked candidates modal on reset
  };

  // Handlers for NLP search
  const handleNLPSearchResults = (data, requestData) => {
    // Transform NLP search results to match mandate search format
    const transformedResults = {
      ...data,
      mandate: null, // NLP search doesn't have a mandate
      results: data.results || [],
      total_results: data.total_results || 0,
      query_tokens: data.query_tokens || 0,
      total_time: data.total_time || 0
    };
    
    setResults(transformedResults);
    setAllCandidates(transformedResults.results);
    setDisplayedCandidates(transformedResults.results.slice(0, 20));
    setDisplayCount(20);
    setShowLinkedCandidates(false);
  };

  const handleNLPError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleNLPLoading = (isLoading) => {
    setLoading(isLoading);
  };

  const handleNLPReset = () => {
    setResults(null);
    setAllCandidates([]);
    setDisplayedCandidates([]);
    setDisplayCount(20);
    setError('');
    setShowLinkedCandidates(false);
  };

  // Modal close handler
  const closeModal = () => setSelectedCandidate(null);

  const handleOpenCandidateModal = async (candidate) => {
    setSelectedCandidate(candidate);
    setLinkedMandates([]);
    setLinkedMandatesError('');
    setLoadingLinkedMandates(true);
    try {
      const data = await apiService.getCandidateLinkedMandates(candidate.id);
      setLinkedMandates(data.mandates);
    } catch (err) {
      setLinkedMandatesError(`Failed to fetch linked mandates: ${err.message}`);
    } finally {
      setLoadingLinkedMandates(false);
    }
  };

  const fetchCandidateDetails = async (candidateId) => {
    try {
      const data = await apiService.getCandidateDetails(candidateId);
      return data; // Should be the full candidate object
    } catch (err) {
      throw err;
    }
  };

  const fetchLinkedCandidates = async () => {
    // This function is now mainly for manual refresh if needed
    if (!results || !results.mandate || !results.mandate.id) {
      setMandateLinkedCandidates([]);
      return;
    }
    setLoadingMandateLinked(true);
    setMandateLinkedError('');
    
    try {
      const data = await apiService.getMandateLinkedCandidates(results.mandate.id);
      setMandateLinkedCandidates(data.candidates || []);
    } catch (err) {
      setMandateLinkedError('Failed to fetch linked candidates');
    } finally {
      setLoadingMandateLinked(false);
    }
  };

  useEffect(() => {
    if (!results || !results.mandate || !results.mandate.id || mandateLinkedCandidates.length === 0) {
      setLinkedCandidatesScores({});
      return;
    }
    const candidateIds = mandateLinkedCandidates.map(c => c.candidate_id);
    
    const fetchMatchScores = async () => {
      try {
        const data = await apiService.getLinkedCandidatesMatchScores(results.mandate.id, candidateIds);
        setLinkedCandidatesScores(data.scores || {});
      } catch (error) {
        setLinkedCandidatesScores({});
      }
    };
    
    fetchMatchScores();
  }, [results && results.mandate && results.mandate.id, mandateLinkedCandidates]);

  const handleShowLinkedCandidates = () => {
    setShowLinkedCandidates(true);
    // Linked candidates are now automatically fetched after search
    // Only refetch if there's an error or explicit need for refresh
  };

  return (
    <Routes>
      <Route path="/" element={
        <PrivateRoute>
          <div className="App">
            <div className="container">
              <Header />

              <TabSystem activeTab={activeTab} setActiveTab={setActiveTab} />

              {activeTab === 'mandate' ? (
                <SearchForm
                  mandateId={mandateId}
                  setMandateId={setMandateId}
                  loading={loading}
                  handleSearch={handleSearch}
                  resetForm={resetForm}
                  allMandates={allMandates}
                  recentMandates={recentMandates}
                />
              ) : (
                <NaturalLanguageSearch
                  onSearchResults={handleNLPSearchResults}
                  onError={handleNLPError}
                  onLoading={handleNLPLoading}
                  onResetResults={handleNLPReset}
                />
              )}

              {error && (
                <div className="error-message">
                  ❌ {error}
                </div>
              )}

              {results && results.mandate && activeTab === 'mandate' && (
                <div className="mandate-section" style={{ background: "#f8f9fa", borderRadius: 12, padding: 24, marginBottom: 24 }}>
                  <h2 style={{ marginBottom: 16 }}>📄 Mandate Details</h2>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 24, width: "100%" }}>
                    <div><strong>ID:</strong> {results.mandate.id}</div>
                    <div><strong>Job Title:</strong> {results.mandate.job_title}</div>
                    <div style={{ width: "100%" }}>
                      <strong>Job Description:</strong>
                      <div
                        style={{ background: "#fff", borderRadius: 8, padding: 12, marginTop: 4 }}
                        dangerouslySetInnerHTML={{ __html: results.mandate.job_desc || "" }}
                      />
                    </div>
                    <div><strong>Primary Skills:</strong> {results.mandate.primary_skills}</div>
                    <div><strong>Secondary Skills:</strong> {results.mandate.secondary_skills}</div>
                    <div><strong>Location:</strong> {results.mandate.location}</div>
                    <div><strong>CTC Range:</strong> {results.mandate.ctc_range_start} - {results.mandate.ctc_range_end} LPA</div>
                    <div><strong>Experience Range:</strong> {results.mandate.exp_range_start} - {results.mandate.exp_range_end} years</div>
                  </div>
                  <button
                    onClick={() => navigate(`/similar-mandates/${results.mandate.id}`)}
                    style={{
                      marginTop: 16,
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Find Similar Mandates
                  </button>
                  {mandateLinkedCandidates.length > 0 && (
                    <button
                      onClick={handleShowLinkedCandidates}
                      style={{
                        marginTop: 16,
                        marginLeft: 16,
                        background: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      Show Linked Candidates
                    </button>
                  )}
                </div>
              )}



              {/* Linked Candidates Modal */}
              {showLinkedCandidates && (
                <div className="modal-overlay" onClick={() => setShowLinkedCandidates(false)} style={{ zIndex: 1000 }}>
                  <div
                    className="modal-content"
                    onClick={e => e.stopPropagation()}
                    tabIndex={-1}
                    style={{ maxWidth: 900 }}
                  >
                    <button className="modal-close" onClick={() => setShowLinkedCandidates(false)} aria-label="Close">&times;</button>
                    <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: "1.15rem" }}>
                      <span role="img" aria-label="link" style={{ marginRight: 8 }}>🔗</span>
                      Candidates Already Linked to This Mandate
                    </h3>
                    {loadingMandateLinked && <div>Loading...</div>}
                    {mandateLinkedError && <div className="error-message">{mandateLinkedError}</div>}
                    {!loadingMandateLinked && !mandateLinkedError && mandateLinkedCandidates.length === 0 && (
                      <div>No candidates are currently linked to this mandate.</div>
                    )}
                    {mandateLinkedCandidates.length > 0 && (
                      <div className="candidates-table-wrapper">
                        <table className="candidates-table">
                          <thead>
                            <tr>
                              <th>Candidate ID</th>
                              <th>Name</th>
                              <th>Status</th>
                              <th>Match Score</th>
                              <th>View</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mandateLinkedCandidates.map((c) => (
                              <tr key={c.candidate_id}>
                                <td>{c.candidate_id}</td>
                                <td>{c.name || '-'}</td>
                                <td>{c.status_name}</td>
                                <td>
                                  {linkedCandidatesScores[c.candidate_id] !== undefined
                                    ? (linkedCandidatesScores[c.candidate_id] * 100).toFixed(1) + '%'
                                    : '...'}
                                </td>
                                <td>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const fullDetails = await fetchCandidateDetails(c.candidate_id);
                                        await handleOpenCandidateModal(fullDetails);
                                      } catch (err) {
                                        alert("Failed to fetch candidate details: " + err.message);
                                      }
                                    }}
                                    style={{
                                      background: "#007bff",
                                      color: "white",
                                      border: "none",
                                      borderRadius: 6,
                                      padding: "4px 8px",
                                      fontSize: "0.9em",
                                      cursor: "pointer",
                                      fontWeight: "bold"
                                    }}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}



              {results && (
                <div className="results-section">
                  <div className="results-header">
                    <h2>🎯 Search Results</h2>
                    <div className="results-stats">
                      <span className="stat">📈 {results.total_results} candidates found</span>
                      <span className="stat">🔢 {results.query_tokens} tokens processed</span>
                      <span className="stat">⚡ {results.total_time.toFixed(2)}s total time</span>
                    </div>
                  </div>
                  <div className="candidates-table-wrapper">
                    <table className="candidates-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Match %</th>
                          <th>Location</th>
                          <th>CTC (LPA)</th>
                          <th>Experience (Years)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedCandidates.map((candidate) => (
                          <tr
                            key={candidate.id}
                            className="candidate-row"
                            onClick={() => handleOpenCandidateModal(candidate)}
                            style={{ cursor: 'pointer' }}
                            tabIndex={0}
                            aria-label={`View details for ${candidate.name || candidate.id}`}
                          >
                            <td>{candidate.id}</td>
                            <td>
                              <span className="candidate-name">
                                {candidate.name || '-'}
                              </span>
                            </td>
                            <td>
                              <span className="match-score-table">
                                {(candidate.score * 100).toFixed(1)}%
                              </span>
                            </td>
                            <td>{candidate.location || '-'}</td>
                            <td>{candidate.ctc ? `${candidate.ctc} LPA` : '-'}</td>
                            <td>{candidate.experience ? `${candidate.experience} years` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {console.log("displayed:", displayedCandidates.length, "all:", allCandidates.length)}
                  {displayedCandidates.length < allCandidates.length && (
                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                      <button
                        className="load-more-btn"
                        onClick={handleLoadMore}
                        disabled={loading}
                      >
                        {loading ? 'Loading...' : 'Load More'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Modal for candidate details */}
              {selectedCandidate && (
                <div className="modal-overlay" onClick={closeModal} style={{ zIndex: 1200 }}>
                  <div
                    className="modal-content"
                    onClick={e => e.stopPropagation()}
                    tabIndex={-1}
                  >
                    <button className="modal-close" onClick={closeModal} aria-label="Close">&times;</button>
                    <h2>
                      {selectedCandidate.name ? (
                        <>
                          <span className="modal-name">{selectedCandidate.name}</span>
                          <span className="modal-id"> (ID: <a 
                            href={`${config.EXTERNAL_CANDIDATE_BASE_URL}${selectedCandidate.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#007bff',
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                            title="Open candidate in external application"
                          >
                            {selectedCandidate.id}
                          </a>)</span>
                        </>
                      ) : (
                        <>ID: <a 
                          href={`${config.EXTERNAL_CANDIDATE_BASE_URL}${selectedCandidate.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#007bff',
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                          title="Open candidate in external application"
                        >
                          {selectedCandidate.id}
                        </a></>
                      )}
                    </h2>
                    <div className="modal-fields">
                      <div className="field-row">
                        <span className="label">📍 Location</span>
                        <span className="value">{selectedCandidate.location || 'Not specified'}</span>
                      </div>
                      <div className="field-row">
                        <span className="label">💰 CTC</span>
                        <span className="value">{selectedCandidate.ctc ? `${selectedCandidate.ctc} LPA` : 'Not specified'}</span>
                      </div>
                      <div className="field-row">
                        <span className="label">🧑‍💼 Experience</span>
                        <span className="value">{selectedCandidate.experience ? `${selectedCandidate.experience} years` : 'Not specified'}</span>
                      </div>
                      <div className="field-row">
                        <span className="label">🛠️ Key Skills</span>
                        <span className="value">{selectedCandidate.key_skills || 'Not specified'}</span>
                      </div>
                      {selectedCandidate.other_skills && (
                        <div className="field-row">
                          <span className="label">🎯 Other Skills</span>
                          <span className="value">{selectedCandidate.other_skills}</span>
                        </div>
                      )}
                      {selectedCandidate.work_summary && (
                        <div className="field-row">
                          <span className="label">📝 Summary</span>
                          <span className="value">{selectedCandidate.work_summary}</span>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <strong>Linked Mandates:</strong>
                      {loadingLinkedMandates && <div>Loading...</div>}
                      {linkedMandatesError && <div className="error-message">{linkedMandatesError}</div>}
                      {(!loadingLinkedMandates && linkedMandates.length === 0 && !linkedMandatesError) && (
                        <div>Not linked to any other mandates.</div>
                      )}
                      {linkedMandates.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {linkedMandates.map((m) => (
                            <li key={m.mandate_id}>
                              <span style={{ fontWeight: "bold" }}>{m.mandate_id}</span>
                              {m.job_title && <> – <span>{m.job_title}</span></>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <button
                      type="button"
                      style={{
                        marginTop: 16,
                        background: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        closeModal();
                        navigate(`/similar-candidates/${selectedCandidate.id}`);
                      }}
                    >
                      Find Similar Candidates
                    </button>
                  </div>
                </div>
              )}


            </div>
          </div>
        </PrivateRoute>
      } />
      <Route path="/similar-candidates/:candidateId" element={
        <PrivateRoute>
          <SimilarCandidatesPage />
        </PrivateRoute>
      } />
      <Route path="/similar-mandates/:mandateId" element={
        <PrivateRoute>
          <SimilarMandatesPage />
        </PrivateRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;